import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Toggle from '../Toggle/Toggle';
import PriceField from '../PriceField/PriceField';
import CONFIG from '../../config/config';

export interface CommissionFeeValue {
  price: number;
  currency: string;
}

interface CommissionTemplateFeeFieldsProps {
  dropFee: CommissionFeeValue;
  renewalFee: CommissionFeeValue;
  onDropFeeChange: (fee: CommissionFeeValue) => void;
  onRenewalFeeChange: (fee: CommissionFeeValue) => void;
  onDropFeePaidChange?: (paid: boolean) => void;
  onRenewalFeePaidChange?: (paid: boolean) => void;
}

export default function CommissionTemplateFeeFields({
  dropFee,
  renewalFee,
  onDropFeeChange,
  onRenewalFeeChange,
  onDropFeePaidChange,
  onRenewalFeePaidChange,
}: CommissionTemplateFeeFieldsProps) {
  const { t } = useTranslation();
  const [isDropFeePaid, setIsDropFeePaid] = useState((dropFee.price ?? 0) > 0);
  const [isRenewalFeePaid, setIsRenewalFeePaid] = useState((renewalFee.price ?? 0) > 0);

  useEffect(() => {
    setIsDropFeePaid((dropFee.price ?? 0) > 0);
  }, [dropFee.price]);

  useEffect(() => {
    setIsRenewalFeePaid((renewalFee.price ?? 0) > 0);
  }, [renewalFee.price]);

  return (
    <div className="space-y-6">
      <div>
        <label className="block mb-2 font-medium">{t('commission_contract_template.drop_fee_label')}</label>
        <div className="flex items-center gap-4 mb-2">
          <Toggle
            label={isDropFeePaid ? t('commission_contract_template.drop_fee.paid') || '' : t('commission_contract_template.drop_fee.free') || ''}
            defaultValue={isDropFeePaid}
            onChange={(checked: boolean) => {
              setIsDropFeePaid(checked);
              onDropFeePaidChange?.(checked);
              if (!checked) {
                onDropFeeChange({ price: 0, currency: dropFee.currency ?? '' });
              }
            }}
          />
          {isDropFeePaid ? (
            <div className="flex items-center gap-2 w-full max-w-xs">
              <PriceField
                label=""
                price={dropFee.price ?? 0}
                currency={dropFee.currency}
                currencies={CONFIG.SUPPORTED_CURRENCIES}
                onCurrencyChange={(currency) => onDropFeeChange({ price: dropFee.price ?? 0, currency })}
                onPriceChange={(price) => onDropFeeChange({ price, currency: dropFee.currency ?? '' })}
              />
            </div>
          ) : (
            <span className="ml-2 text-sm text-muted-foreground">{t('commission_contract_template.drop_fee.free')}</span>
          )}
        </div>
      </div>

      <div>
        <label className="block mb-2 font-medium">{t('commission_contract_template.renewal_fee_label')}</label>
        <div className="flex items-center gap-4 mb-2">
          <Toggle
            label={isRenewalFeePaid ? t('commission_contract_template.renewal_fee.paid') || '' : t('commission_contract_template.renewal_fee.free') || ''}
            defaultValue={isRenewalFeePaid}
            onChange={(checked: boolean) => {
              setIsRenewalFeePaid(checked);
              onRenewalFeePaidChange?.(checked);
              if (!checked) {
                onRenewalFeeChange({ price: 0, currency: renewalFee.currency ?? '' });
              }
            }}
          />
          {isRenewalFeePaid ? (
            <div className="flex items-center gap-2 w-full max-w-xs">
              <PriceField
                label=""
                price={renewalFee.price ?? 0}
                currency={renewalFee.currency}
                currencies={CONFIG.SUPPORTED_CURRENCIES}
                onCurrencyChange={(currency) => onRenewalFeeChange({ price: renewalFee.price ?? 0, currency })}
                onPriceChange={(price) => onRenewalFeeChange({ price, currency: renewalFee.currency ?? '' })}
              />
            </div>
          ) : (
            <span className="ml-2 text-sm text-muted-foreground">{t('commission_contract_template.renewal_fee.free')}</span>
          )}
        </div>
      </div>
    </div>
  );
}
